/**
 * Image Utilities for LLM API Calls
 *
 * Handles image resizing and optimization for AI provider limits.
 * Based on Claude/Anthropic's documented image requirements:
 * - Maximum dimensions: 8000x8000 (or 2000x2000 for >20 images)
 * - Optimal dimensions: 1568px max, 1.15 megapixels
 * - Token calculation: (width * height) / 750
 *
 * These limits are conservative and work across major providers
 * (Anthropic, OpenAI, Google, etc.)
 */
// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════
/** Claude's maximum image dimension (8000x8000) */
export const MAX_IMAGE_DIMENSION = 8000;
/** Claude's maximum dimension when >20 images in request */
export const MAX_IMAGE_DIMENSION_MULTIPLE = 2000;
/** Claude's recommended maximum dimension for optimal performance */
export const MAX_OPTIMAL_DIMENSION = 1568;
/** Claude's recommended maximum megapixels (1.15 MP) */
export const MAX_RECOMMENDED_MEGAPIXELS = 1.15;
/** Minimum dimension for good results */
export const MIN_RECOMMENDED_DIMENSION = 200;
/** Tokens per image calculation divisor (Claude's formula) */
export const TOKENS_PER_PIXEL_DIVISOR = 750;
// ═══════════════════════════════════════════════════════════════════════════
// MIME TYPE DETECTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Infer image MIME type from base64 content.
 * Accepts raw base64 or data URL strings.
 */
export function detectImageMimeType(base64) {
    const dataUrlMatch = base64.match(/^data:([^;]+);base64,(.+)$/);
    const normalizedBase64 = dataUrlMatch ? dataUrlMatch[2] : base64;
    const header = normalizedBase64.slice(0, 12);
    // PNG: 89 50 4E 47 0D 0A 1A 0A => iVBORw0KGgo
    if (header.startsWith('iVBORw0KGgo')) {
        return 'image/png';
    }
    // JPEG: FF D8 FF => /9j/
    if (header.startsWith('/9j/')) {
        return 'image/jpeg';
    }
    // GIF: 47 49 46 38 => R0lGOD
    if (header.startsWith('R0lGOD')) {
        return 'image/gif';
    }
    // WEBP: "RIFF"...."WEBP" => UklGR
    if (header.startsWith('UklGR')) {
        return 'image/webp';
    }
    return null;
}
// ═══════════════════════════════════════════════════════════════════════════
// DIMENSION CALCULATIONS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Determine the maximum allowed dimension based on context
 *
 * @param model - Model name (e.g., "anthropic/claude-3-5-sonnet")
 * @param imageCount - Number of images in the request
 * @returns Maximum dimension allowed
 */
export function getMaxDimension(model, imageCount = 1) {
    // Use reduced limit for >20 images with Claude
    if (imageCount > 20 && model && isAnthropicModel(model)) {
        return MAX_IMAGE_DIMENSION_MULTIPLE;
    }
    return MAX_IMAGE_DIMENSION;
}
/**
 * Check if a model is an Anthropic/Claude model
 */
export function isAnthropicModel(model) {
    const lowerModel = model.toLowerCase();
    return lowerModel.includes('anthropic') ||
        lowerModel.includes('claude') ||
        lowerModel.includes('bedrock'); // AWS Bedrock Claude
}
/**
 * Calculate optimal dimensions for an image
 *
 * Two-pass approach:
 * 1. Check hard maximum limits (8000px or 2000px for batch)
 * 2. Check optimal limits (1568px, 1.15 megapixels)
 *
 * @param width - Original image width
 * @param height - Original image height
 * @param model - Model name for provider-specific limits
 * @param imageCount - Number of images in the request
 * @returns Optimal dimensions (may be same as original if no resize needed)
 */
export function calculateOptimalDimensions(width, height, model, imageCount = 1) {
    const maxDim = getMaxDimension(model, imageCount);
    const aspectRatio = width / height;
    // Pass 1: Check hard maximum limits
    if (width > maxDim || height > maxDim) {
        if (width > height) {
            return {
                width: maxDim,
                height: Math.round(maxDim / aspectRatio)
            };
        }
        else {
            return {
                width: Math.round(maxDim * aspectRatio),
                height: maxDim
            };
        }
    }
    // Pass 2: Check optimal dimensions for performance
    const megapixels = (width * height) / 1000000;
    if (width > MAX_OPTIMAL_DIMENSION ||
        height > MAX_OPTIMAL_DIMENSION ||
        megapixels > MAX_RECOMMENDED_MEGAPIXELS) {
        if (megapixels > MAX_RECOMMENDED_MEGAPIXELS) {
            // Resize to target megapixels while preserving aspect ratio
            const scaleFactor = Math.sqrt(MAX_RECOMMENDED_MEGAPIXELS / megapixels);
            let newWidth = Math.round(width * scaleFactor);
            let newHeight = Math.round(height * scaleFactor);
            // Ensure we don't exceed MAX_OPTIMAL_DIMENSION on any side
            if (newWidth > MAX_OPTIMAL_DIMENSION) {
                newWidth = MAX_OPTIMAL_DIMENSION;
                newHeight = Math.round(newWidth / aspectRatio);
            }
            else if (newHeight > MAX_OPTIMAL_DIMENSION) {
                newHeight = MAX_OPTIMAL_DIMENSION;
                newWidth = Math.round(newHeight * aspectRatio);
            }
            return { width: newWidth, height: newHeight };
        }
        // Dimension exceeds optimal but not megapixels
        if (width > MAX_OPTIMAL_DIMENSION) {
            return {
                width: MAX_OPTIMAL_DIMENSION,
                height: Math.round(MAX_OPTIMAL_DIMENSION / aspectRatio)
            };
        }
        if (height > MAX_OPTIMAL_DIMENSION) {
            return {
                width: Math.round(MAX_OPTIMAL_DIMENSION * aspectRatio),
                height: MAX_OPTIMAL_DIMENSION
            };
        }
    }
    // Image is within acceptable limits
    return { width, height };
}
/**
 * Check if an image needs resizing
 */
export function needsResize(width, height, model, imageCount = 1) {
    const optimal = calculateOptimalDimensions(width, height, model, imageCount);
    return optimal.width !== width || optimal.height !== height;
}
// ═══════════════════════════════════════════════════════════════════════════
// TOKEN ESTIMATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Estimate token count for an image based on Claude's algorithm
 *
 * Formula: tokens = (width × height) / 750
 *
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @returns Estimated token count
 */
export function estimateImageTokens(width, height) {
    return Math.ceil((width * height) / TOKENS_PER_PIXEL_DIVISOR);
}
/**
 * Estimate total tokens for multiple images
 */
export function estimateTotalImageTokens(images) {
    return images.reduce((total, img) => total + estimateImageTokens(img.width, img.height), 0);
}
/**
 * Calculate how many tokens an image will use after optimal resizing
 */
export function estimateOptimalTokens(width, height, model, imageCount = 1) {
    const optimal = calculateOptimalDimensions(width, height, model, imageCount);
    return estimateImageTokens(optimal.width, optimal.height);
}
// ═══════════════════════════════════════════════════════════════════════════
// IMAGE PROCESSING (Browser Environment)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Load an image from base64 data
 *
 * @param base64 - Base64-encoded image data (without data URI prefix)
 * @param mimeType - Image MIME type (e.g., 'image/png')
 * @returns Promise resolving to HTMLImageElement
 */
export async function loadImageFromBase64(base64, mimeType) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(new Error(`Failed to load image: ${err}`));
        img.src = `data:${mimeType};base64,${base64}`;
    });
}
/**
 * Get dimensions from a base64 image without fully loading it
 * Uses a lightweight approach when possible
 */
export async function getImageDimensions(base64, mimeType) {
    const img = await loadImageFromBase64(base64, mimeType);
    return { width: img.width, height: img.height };
}
/**
 * Resize an image using canvas
 *
 * Uses high-quality image scaling with imageSmoothingQuality set to 'high'
 * (equivalent to Python's Image.LANCZOS)
 *
 * @param img - Source image element
 * @param targetWidth - Target width
 * @param targetHeight - Target height
 * @param mimeType - Output MIME type
 * @param quality - JPEG quality (0-1), ignored for PNG
 * @returns Base64-encoded resized image (without data URI prefix)
 */
export function resizeImageWithCanvas(img, targetWidth, targetHeight, mimeType = 'image/png', quality = 0.92) {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get canvas 2D context');
    }
    // Use high-quality scaling (similar to LANCZOS)
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    // Get base64 data (remove the data URI prefix)
    const dataUrl = canvas.toDataURL(mimeType, quality);
    return dataUrl.split(',')[1];
}
/**
 * Process and resize an image if needed
 *
 * Main function for image processing. Will:
 * 1. Load the image to get dimensions
 * 2. Calculate optimal dimensions
 * 3. Resize if necessary
 * 4. Return processed image with metadata
 *
 * @param base64 - Base64-encoded image data
 * @param mimeType - Image MIME type
 * @param model - Model name for provider-specific limits
 * @param imageCount - Number of images in the request
 * @returns Processed image with metadata
 */
export async function processImage(base64, mimeType, model, imageCount = 1) {
    try {
        // Load image to get dimensions
        const img = await loadImageFromBase64(base64, mimeType);
        const originalWidth = img.width;
        const originalHeight = img.height;
        // Calculate optimal dimensions
        const optimal = calculateOptimalDimensions(originalWidth, originalHeight, model, imageCount);
        // Check if resize is needed
        if (optimal.width === originalWidth && optimal.height === originalHeight) {
            // No resize needed
            return {
                base64,
                width: originalWidth,
                height: originalHeight,
                wasResized: false,
                estimatedTokens: estimateImageTokens(originalWidth, originalHeight)
            };
        }
        // Resize the image
        console.log(`[ImageUtils] Resizing image from ${originalWidth}x${originalHeight} to ${optimal.width}x${optimal.height}`);
        const resizedBase64 = resizeImageWithCanvas(img, optimal.width, optimal.height, mimeType);
        return {
            base64: resizedBase64,
            width: optimal.width,
            height: optimal.height,
            wasResized: true,
            estimatedTokens: estimateImageTokens(optimal.width, optimal.height)
        };
    }
    catch (error) {
        console.error('[ImageUtils] Error processing image:', error);
        // Return original image if processing fails
        return {
            base64,
            width: 0,
            height: 0,
            wasResized: false,
            estimatedTokens: 0
        };
    }
}
/**
 * Process multiple images, respecting token limits
 *
 * @param images - Array of {base64, mimeType} objects
 * @param maxTotalTokens - Maximum total tokens for all images (optional)
 * @param model - Model name
 * @returns Array of processed images that fit within constraints
 */
export async function processImages(images, maxTotalTokens, model) {
    const imageCount = images.length;
    const processed = [];
    let totalTokens = 0;
    for (const { base64, mimeType } of images) {
        const result = await processImage(base64, mimeType, model, imageCount);
        // Check token limit if specified
        if (maxTotalTokens && totalTokens + result.estimatedTokens > maxTotalTokens) {
            console.log(`[ImageUtils] Skipping image - would exceed token limit (${totalTokens + result.estimatedTokens} > ${maxTotalTokens})`);
            continue;
        }
        processed.push(result);
        totalTokens += result.estimatedTokens;
    }
    if (processed.length < images.length) {
        console.log(`[ImageUtils] Pruned images from ${images.length} to ${processed.length} (total tokens: ${totalTokens})`);
    }
    return processed;
}
// ═══════════════════════════════════════════════════════════════════════════
// SYNC UTILITIES (for when async isn't possible)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Synchronously check if resize would be needed based on metadata
 *
 * Use this when you have dimension metadata but can't do async processing
 */
export function wouldNeedResize(metadata, model, imageCount = 1) {
    if (!metadata?.width || !metadata?.height) {
        return false; // Can't determine without dimensions
    }
    return needsResize(metadata.width, metadata.height, model, imageCount);
}
/**
 * Get estimated tokens from metadata (sync)
 */
export function getTokensFromMetadata(metadata) {
    if (metadata?.estimatedTokens) {
        return metadata.estimatedTokens;
    }
    if (metadata?.width && metadata?.height) {
        return estimateImageTokens(metadata.width, metadata.height);
    }
    // Default fallback for unknown dimensions
    // Assume a typical 1024x768 image
    return estimateImageTokens(1024, 768);
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
export default {
    // Constants
    MAX_IMAGE_DIMENSION,
    MAX_IMAGE_DIMENSION_MULTIPLE,
    MAX_OPTIMAL_DIMENSION,
    MAX_RECOMMENDED_MEGAPIXELS,
    MIN_RECOMMENDED_DIMENSION,
    TOKENS_PER_PIXEL_DIVISOR,
    // Functions
    getMaxDimension,
    isAnthropicModel,
    calculateOptimalDimensions,
    needsResize,
    estimateImageTokens,
    estimateTotalImageTokens,
    estimateOptimalTokens,
    loadImageFromBase64,
    getImageDimensions,
    resizeImageWithCanvas,
    processImage,
    processImages,
    wouldNeedResize,
    getTokensFromMetadata,
};
